// CustomerHelp renderer — reads from the public RPCs defined alongside
// the WI-108 RAG pipeline and exposes them as TanStack Query hooks.
//
// Articles live in public.customer_help_articles. The two SECDEF RPCs
// public.customer_help_articles_list and public.customer_help_article_by_slug
// are granted to anon/authenticated, so the Hub can call them with
// the publishable key (no auth required) — owner, operator, pro, and
// lead audiences all read from the same surface (WI-113, Peter's
// 2026-05-26 decision: pkntops.com/help/<slug> is the one renderer).

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { isCell } from './cell/config';
import { cellGet } from './cell/api';

export interface HelpArticleListItem {
  slug: string;
  title: string;
  surface: string;
  audience: string | null;
  priority: string | null;
  updated_at: string;
}

export interface HelpArticle {
  slug: string;
  title: string;
  surface: string;
  audience: string | null;
  priority: string | null;
  body_md: string;
  metadata: Record<string, unknown> | null;
  published_at: string | null;
  updated_at: string;
  location_id: string | null;
  location_config: LocationConfig | null;
  kb_topics: Record<string, string>;
}

export interface LocationConfig {
  location_id: string;
  display_name?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  timezone?: string | null;
  hub_subdomain?: string | null;
  owner_email?: string | null;
  owner_display_name?: string | null;
  owner_send_domain?: string | null;
  assistant_name?: string | null;
  wifi?: { ssid?: string; password?: string } | null;
  docs?: unknown;
  social?: unknown;
  hub_branding?: unknown;
  resources_url?: string | null;
  resources_label?: string | null;
}

export function useHelpArticleList(surface?: string) {
  return useQuery({
    queryKey: ['help', 'list', surface ?? null],
    queryFn: async (): Promise<HelpArticleListItem[]> => {
      // CELL (M28): GET /v1/pro-hub/help (signed in on AWS; anonymous help is the support unit's, M27).
      if (isCell()) { const r = await cellGet<{ rows: HelpArticleListItem[] }>('/v1/pro-hub/help', { surface: surface ?? null }); if (r.error) throw new Error(r.error.message); return r.data?.rows ?? []; }
      const { data, error } = await supabase.rpc('customer_help_articles_list', {
        p_surface: surface ?? null,
      });
      if (error) throw error;
      return (data ?? []) as HelpArticleListItem[];
    },
    staleTime: 60_000,
  });
}

export function useHelpArticle(slug: string | null, locationId: string | null) {
  return useQuery({
    queryKey: ['help', 'article', slug, locationId],
    enabled: !!slug,
    queryFn: async (): Promise<HelpArticle | null> => {
      if (!slug) return null;
      // CELL (M28): GET /v1/pro-hub/help/article
      if (isCell()) { const r = await cellGet<{ rows: HelpArticle[] }>('/v1/pro-hub/help/article', { slug, location: locationId }); if (r.error) throw new Error(r.error.message); return r.data?.rows?.[0] ?? null; }
      const { data, error } = await supabase.rpc('customer_help_article_by_slug', {
        p_slug: slug,
        p_location_id: locationId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as HelpArticle | null;
    },
    staleTime: 60_000,
  });
}

// Mustache-style {{token}} interpolation. Used at render time so articles
// can stay location-agnostic on disk and pick up per-location values
// from location_configs + kira_knowledge (per CATALOG.md Q3, 2026-05-26).
//
// Supported tokens:
//   {{location.<field>}}        — any scalar field on the location_config
//                                  payload returned by the RPC
//                                  (display_name, phone, address, email,
//                                  owner_display_name, assistant_name, …)
//   {{kb.<topic>}}              — kira_knowledge.content for the given
//                                  topic, scoped to the article's
//                                  location (e.g. {{kb.body-art-permits}})
//
// Unknown / unresolved tokens render as an empty string so a missing
// per-location KB row doesn't leak `{{kb.foo}}` to a tenant's screen.
// The renderer warns to the console so an authoring mistake surfaces
// during dev review.
export function interpolate(
  body: string,
  vars: { location?: LocationConfig | null; kb?: Record<string, string> | null },
): string {
  return body.replace(/\{\{\s*([a-z]+)\.([a-z0-9_.\-]+)\s*\}\}/gi, (raw, ns: string, key: string) => {
    const lns = ns.toLowerCase();
    if (lns === 'location') {
      const v = (vars.location ?? null) as Record<string, unknown> | null;
      if (!v) return '';
      const val = resolvePath(v, key);
      if (val == null) {
        // eslint-disable-next-line no-console
        console.warn(`[help] unresolved location token: ${raw}`);
        return '';
      }
      return String(val);
    }
    if (lns === 'kb') {
      const topic = key;
      const content = vars.kb?.[topic];
      if (!content) {
        // eslint-disable-next-line no-console
        console.warn(`[help] unresolved kb token: ${raw}`);
        return '';
      }
      return content;
    }
    return '';
  });
}

function resolvePath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

// Catalog surface labels for the index page grouping.
export const SURFACE_LABELS: Record<string, string> = {
  ios: 'PKNT Ops app (iOS)',
  hub: 'Pro Hub',
  multi: 'Cross-surface',
  ghl: 'Booking & lead flows',
};

export function surfaceLabel(surface: string): string {
  return SURFACE_LABELS[surface] ?? surface;
}
